"use client";

import { InputField } from "@/components/ui/input-field";
import { useMediaQuery } from "@/hooks/use-media-query";
import React from "react";

type FormData = {
  studioDetails: {
    studioName: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    studioType: string;
    postcode: string;
    equipmentDetails: string;
  };
};

interface StudioDetailsFormProps {
  formData: FormData;
  updateFormData: (data: Partial<FormData>) => void;
  formErrors: Record<string, string>;
  setFormErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}

export function StudioDetailsForm({
  formData,
  updateFormData,
  formErrors,
  setFormErrors,
}: StudioDetailsFormProps) {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const handleChange = (field: keyof FormData['studioDetails']) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
    updateFormData({
      studioDetails: {
        ...formData.studioDetails,
        [field]: e.target.value
      }
    });
  };

  return (
    <div className={isMobile ? "space-y-4" : "space-y-6"}>
      <div className="space-y-4">
        <InputField
          label="Studio Name"
          value={formData.studioDetails.studioName}
          onChange={handleChange('studioName')}
          placeholder="Enter your studio's name (optional)"
          error={formErrors.studioName}
        />

        <InputField
          label="Street Address"
          value={formData.studioDetails.address}
          onChange={handleChange('address')}
          placeholder="Enter your studio's street address (optional)"
          error={formErrors.address}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InputField
            label="City"
            value={formData.studioDetails.city}
            onChange={handleChange('city')}
            placeholder="City (optional)"
            error={formErrors.city}
          />
          <InputField
            label="State/Region"
            value={formData.studioDetails.state}
            onChange={handleChange('state')}
            placeholder="State or Region (optional)"
            error={formErrors.state}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InputField
            label="Postal Code"
            value={formData.studioDetails.postcode}
            onChange={handleChange('postcode')}
            placeholder="Postal Code (optional)"
            error={formErrors.postcode}
          />
          <InputField
            label="ZIP Code"
            value={formData.studioDetails.zipCode}
            onChange={handleChange('zipCode')}
            placeholder="ZIP Code (optional)"
            error={formErrors.zipCode}
          />
        </div>

        <InputField
          label="Studio Type"
          value={formData.studioDetails.studioType}
          onChange={handleChange('studioType')}
          placeholder="e.g., Home food creator, Commercial studio, Food truck (optional)"
          error={formErrors.studioType}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Equipment Details
          </label>
          <textarea
            value={formData.studioDetails.equipmentDetails}
            onChange={handleChange('equipmentDetails')}
            placeholder="Describe your studio equipment and setup (optional)"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ff3b30] focus:border-transparent"
            rows={3}
          />
        </div>


      </div>
    </div>
  );
} 
